import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, TextField, Button, MenuItem
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import ApiService from '../../services/api.service';

const FS = '0.8125rem';
// 13px fields like the rest of the app.
const FIELD_SX = { '& .MuiInputBase-root': { fontSize: FS }, '& .MuiInputLabel-root': { fontSize: FS } };

const PortalServiceSettings = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadFieldDefaults();
  }, []);

  const loadFieldDefaults = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ApiService.getPortalFieldDefaults();
      const data = response?.data?.data || [];
      // Flat catalog of custom fields declared by the TC, each with the org's saved default (if any).
      setFields(data.map(f => ({
        fieldId: f.fieldId || f.field_id || f.id,
        code: f.code,
        label: f.label,
        fieldType: f.fieldType || f.field_type || 'TEXT',
        options: f.options || [],
        placeholder: f.placeholder || '',
        orgDefault: f.orgDefault || f.org_default || ''
      })));
    } catch (err) {
      setError('Failed to load service settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldIdx, value) => {
    setFields(prev => prev.map((f, i) => (i === fieldIdx ? { ...f, orgDefault: value } : f)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await ApiService.savePortalFieldDefaults(
        fields.map(f => ({ fieldId: f.fieldId, defaultValue: f.orgDefault }))
      );
      setSuccess('Default values saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save defaults');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      <Box sx={{ maxWidth: 800 }}>
        <Typography sx={{ fontSize: FS, color: 'text.secondary', mb: 1.5 }}>
          Set default values for your organization&apos;s custom fields. When your typing center creates a
          work order for your organization, these defaults are pre-filled automatically.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {fields.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: FS, color: 'text.secondary' }}>
              No custom fields are available for your organization.
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, ...FIELD_SX }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
              Default values
            </Typography>
            {fields.map((field, fieldIdx) => (
              <Box key={field.fieldId} sx={{ mb: 1.5 }}>
                {field.fieldType === 'SELECT' ? (
                  <TextField
                    select fullWidth size="small"
                    label={field.label}
                    value={field.orgDefault || ''}
                    onChange={(e) => handleFieldChange(fieldIdx, e.target.value)}
                  >
                    <MenuItem value="" sx={{ fontSize: FS }}><em>None</em></MenuItem>
                    {(field.options || []).map((opt, i) => (
                      <MenuItem key={i} sx={{ fontSize: FS }} value={typeof opt === 'string' ? opt : (opt.value || '')}>
                        {typeof opt === 'string' ? opt : (opt.label || opt.value || '')}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : field.fieldType === 'DATE' ? (
                  <TextField
                    fullWidth size="small" type="date"
                    label={field.label}
                    value={field.orgDefault || ''}
                    onChange={(e) => handleFieldChange(fieldIdx, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                ) : (
                  <TextField
                    fullWidth size="small"
                    type={field.fieldType === 'NUMBER' ? 'number' : 'text'}
                    multiline={field.fieldType === 'TEXTAREA'}
                    rows={field.fieldType === 'TEXTAREA' ? 2 : undefined}
                    label={field.label}
                    value={field.orgDefault || ''}
                    onChange={(e) => handleFieldChange(fieldIdx, e.target.value)}
                    placeholder={field.placeholder || ''}
                  />
                )}
              </Box>
            ))}
            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ textTransform: 'none', fontSize: FS }}
              >
                {saving ? 'Saving...' : 'Save Defaults'}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default PortalServiceSettings;
